import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  FormHelperText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { getProducts } from "../../../api/Product/api";
import { X } from "lucide-react";

const STOCK_STATUS = {
  AVAILABLE: "AVAILABLE",
  UNAVAILABLE: "UNAVAILABLE",
};

const StockForm = ({ initialData, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState(
    initialData
      ? {
          product: initialData.product_id || "",
          available_quantity: initialData.available_quantity || "",
          reserved_quantity: initialData.reserved_quantity || "",
          serving_warehouse: initialData.serving_warehouse || "",
          stock_date: initialData.stock_date || "",
          stock_region: initialData.stock_region || "",
          status: initialData.status || STOCK_STATUS.AVAILABLE,
        }
      : {
          product: "",
          available_quantity: "",
          reserved_quantity: "",
          serving_warehouse: "",
          stock_date: "",
          stock_region: "",
          status: STOCK_STATUS.AVAILABLE,
        }
  );

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({
    product: "",
    available_quantity: "",
    reserved_quantity: "",
    serving_warehouse: "",
    stock_date: "",
    stock_region: "",
  });



  const getProduct = async () => {
    try {
      setLoading(true);
      const response = await getProducts();
      if (response?.data) {
        setProducts(response.data);
      }
    } catch (err) {
      console.error("Error loading Products:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProduct();
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData({
        product: initialData.product_id || "",
        available_quantity: initialData.available_quantity || "",
        reserved_quantity: initialData.reserved_quantity || "",
        serving_warehouse: initialData.serving_warehouse || "",
        stock_date: initialData.stock_date
          ? initialData.stock_date.split("T")[0]
          : "",
        stock_region: initialData.stock_region || "",
        status: initialData.status || STOCK_STATUS.AVAILABLE,
      });
    }
  }, [initialData]);

  const validateForm = () => {
    let newErrors = {
      product: "",
      available_quantity: "",
      reserved_quantity: "",
      serving_warehouse: "",
      stock_date: "",
      stock_region: "",
    };

    let isValid = true;

    if (!formData.available_quantity) {
      newErrors.available_quantity = "Available quantity is required";
      isValid = false;
    } else if (
      isNaN(formData.available_quantity) ||
      formData.available_quantity <= 0
    ) {
      newErrors.available_quantity =
        "Available quantity must be greater than 0"
      isValid = false;
    }
    if (!formData.reserved_quantity) {
      newErrors.reserved_quantity = "Reserved quantity is required";
      isValid = false;
    } else if (
      isNaN(formData.reserved_quantity) ||
      formData.reserved_quantity < 0
    ) {
      newErrors.reserved_quantity =
        "Reserved quantity must be a positive number";
      isValid = false;
    }
    if (!formData.serving_warehouse.trim()) {
      newErrors.serving_warehouse = "Serving warehouse is required";
      isValid = false;
    }
    if (!formData.stock_date) {
      newErrors.stock_date = "Stock date is required";
      isValid = false;
    }
    if (!formData.stock_region.trim()) {
      newErrors.stock_region = "Stock region is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return; // Stop submission if validation fails
    }

    onSubmit(formData);
  };

  return (
    <Box sx={{ p: 2 }}>
      <DialogTitle
        sx={{
          p: 1,
          fontSize: 18,
          fontWeight: 500,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {initialData ? "Edit Stock" : "Add Stock"}
        <IconButton
          onClick={onCancel}
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>


          <Grid item xs={12}>
            <FormControl fullWidth required error={!!errors.product}>
              <InputLabel id="product-label">Product</InputLabel>
              <Select
                labelId="product-label"
                value={formData.product}
                onChange={(e) =>
                  setFormData({ ...formData, product: e.target.value })
                }
                label="Product"
                disabled={loading}
              >
                {products.map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                  {product.code} | {product.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.product && (
                <FormHelperText>{errors.product}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Available Quantity"
              type="number"
              value={formData.available_quantity}
              onChange={(e) =>
                setFormData({ ...formData, available_quantity: e.target.value })
              }
              required
              error={Boolean(errors.available_quantity)}
              helperText={errors.available_quantity}
              InputProps={{ inputProps: { min: 1 } }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Reserved Quantity"
              type="number"
              value={formData.reserved_quantity}
              onChange={(e) =>
                setFormData({ ...formData, reserved_quantity: e.target.value })
              }
              required
              error={Boolean(errors.reserved_quantity)}
              helperText={errors.reserved_quantity}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Serving Warehouse"
              value={formData.serving_warehouse}
              onChange={(e) =>
                setFormData({ ...formData, serving_warehouse: e.target.value })
              }
              required
              error={Boolean(errors.serving_warehouse)}
              helperText={errors.serving_warehouse}
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth required>
              <TextField
                label="Stock Date"
                type="date"
                value={formData.stock_date}
                onChange={(e) =>
                  setFormData({ ...formData, stock_date: e.target.value })
                }
                required
                error={Boolean(errors.stock_date)}
                helperText={errors.stock_date}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth required>
              <TextField
                label="Stock Region"
                value={formData.stock_region}
                onChange={(e) =>
                  setFormData({ ...formData, stock_region: e.target.value })
                }
                required
                error={Boolean(errors.stock_region)}
                helperText={errors.stock_region}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                name="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                label="Status"
                required
              >
                {Object.entries(STOCK_STATUS).map(([key, value]) => (
                  <MenuItem key={key} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={onCancel}
                sx={{
                  bgcolor: "#e0e0e0",
                  color: "#000",
                  "&:hover": { bgcolor: "#d5d5d5" },
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  fontSize: "16px",
                  lineHeight: "24px",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                sx={{
                  bgcolor: "#00694B",
                  color: "#fff",
                  "&:hover": { bgcolor: "#005039" },
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  fontSize: "16px",
                  lineHeight: "24px",
                }}
              >
                {initialData ? "Update" : "Add Stock"}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default StockForm;
